import React, { useEffect, useRef, useState } from "react";
import Select from "react-select";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import "../../assets/styles/createproduct.css";
import { RiDeleteBin5Line } from "react-icons/ri";
import Modal from "../../components/common/modal";
import { toast } from "react-toastify";
import { useViewTagsList } from "../../hooks/tags/tags.list";
import { useViewIndustryList } from "../../hooks/industries/industrylist";
import { useViewMacrofamily } from "../../hooks/macrofamily/macro.showall";
import { useViewCategoryList } from "../../hooks/categorys/category.list";
import { useProductUpdate } from "../../hooks/products/products.update";
import { useViewSingleProduct } from "../../hooks/products/product.show";


const normalizeId = (value) => {
  if (!value) return "";
  if (typeof value === "string") return value;
  return (
    value._id ||  
    value.id ||
    value.value ||
    value.slug ||
    value.code ||
    ""
  );
};

const buildAssetUrl = (path) => {
  if (!path) return "";
  let normalized = String(path).replace(/\\/g, "/");
  
  normalized = normalized.replace(/^uploads(?!\/)/, "uploads/");
  const base = process.env.REACT_APP_BASE_IMAGE;
  return base ? `${base}/${normalized}` : normalized;
};

const getFileName = (path) => {
  if (!path) return "";
  const segments = String(path).split(/[\\/]/);
  return segments[segments.length - 1] || "";
};

const getOptionLabel = (option) => {
  if (!option) return "";
  if (typeof option === "string") return option;
  return option.title || option.name || option.label || "";
};


const extractMediaPath = (entry) => {
  if (!entry) return "";
  if (typeof entry === "string") return entry;
  if (typeof entry === "object") {
    return (
      entry.path ||
      entry.url ||
      entry.filePath ||
      entry.location ||
      entry.filename ||
      ""
    );
  }
  return "";
};

const normalizeVideoEntry = (video) => {
  if (!video) {
    return { url: "", title: "", description: "" };
  }

  if (typeof video === "string") {
    return { url: video, title: "", description: "" };
  }

  return {
    url: video?.url || "",
    title: video?.title || "",
    description: video?.description || "",
  };
};

const fetchAssetAsFile = async (path, fallbackPrefix) => {
  const normalizedPath = extractMediaPath(path);
  if (!normalizedPath) return { type: "path", value: "" };
  const assetUrl = buildAssetUrl(normalizedPath);
  if (!assetUrl) {
    return { type: "path", value: normalizedPath };
  }

  try {
    const response = await fetch(assetUrl);
    if (!response.ok) {
      throw new Error(`Failed to fetch asset: ${response.status}`);
    }
    const blob = await response.blob();
    const filename = getFileName(normalizedPath) || `${fallbackPrefix || "asset"}.bin`;
    const file = new File([blob], filename, {
      type: blob.type || "application/octet-stream",
    });
    return { type: "file", value: file };
  } catch (error) {
    console.warn("Unable to refetch asset for upload", normalizedPath, error);
    return { type: "path", value: normalizedPath };
  }
};

const convertExistingAssets = async (paths, prefix) => {
  if (!Array.isArray(paths) || paths.length === 0) return [];
  const results = await Promise.all(
    paths.map((entry) => fetchAssetAsFile(entry, prefix))
  );
  return results.filter((item) => item && (item.value || item.value === ""));
};



const EditProducts = () => {
  const { id: routeId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();

  const seededProduct = location.state?.product;
  const productId =
    routeId ||
    seededProduct?._id ||
    seededProduct?.id ||
    seededProduct?.productId ||
    "";

  const { data: productData, isLoading: productLoading } =
    useViewSingleProduct(productId, !!productId);

  const { data: categoriesData } = useViewCategoryList();
  const { data: industriesData } = useViewIndustryList();
  const { data: macrofamiliesData } = useViewMacrofamily();
  const { data: tagsData } = useViewTagsList();
  const { mutate: updateProduct } = useProductUpdate();

  const [title, setTitle] = useState("");
  const [slug, setSlug] = useState("");
  // Refs for validation
  const titleRef = useRef(null);
  const slugRef = useRef(null);
  const [macroFamilyId, setMacroFamilyId] = useState([]);
  const [categoryId, setCategoryId] = useState("");
  const [industryId, setIndustryId] = useState([]);
  const [tags, setTags] = useState([]);

  const [thumbnailFile, setThumbnailFile] = useState(null);
  const [thumbnailPreview, setThumbnailPreview] = useState("");

  const [existingImages, setExistingImages] = useState([]);
  const [newImageFiles, setNewImageFiles] = useState([]);
  const [newImagePreviews, setNewImagePreviews] = useState([]);

  const [existingCatalogs, setExistingCatalogs] = useState([]);
  const [existingCatalogTitles, setExistingCatalogTitles] = useState([]);
  const [existingCatalogDescriptions, setExistingCatalogDescriptions] = useState([]);
  const [newCatalogFiles, setNewCatalogFiles] = useState([]);
  const [newCatalogPreviews, setNewCatalogPreviews] = useState([]);
  const [newCatalogTitles, setNewCatalogTitles] = useState([]);
  const [newCatalogDescriptions, setNewCatalogDescriptions] = useState([]);

  const [videoEntries, setVideoEntries] = useState([]);
  const [videoUrlInput, setVideoUrlInput] = useState("");
  const [videoTitleInput, setVideoTitleInput] = useState("");
  const [videoDescriptionInput, setVideoDescriptionInput] = useState("");

  const [selectedImage, setSelectedImage] = useState("");
  const [selectedImageMeta, setSelectedImageMeta] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const [formInitializing, setFormInitializing] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [isTagDropdownOpen, setIsTagDropdownOpen] = useState(false);

  const tagDropdownRef = useRef(null);
  const loadErrorNotified = useRef(false);

  const macrofamilyOptions = macrofamiliesData?.data?.macros || [];
  const categoryOptions = categoriesData?.data || categoriesData?.data?.data || [];
  const industryOptions = industriesData?.data || industriesData?.data?.data || [];
  const tagOptions =
    tagsData?.data?.tags ||
    tagsData?.data ||
    tagsData?.tags ||
    [];

  const selectMenuPortalTarget =
    typeof document !== "undefined" ? document.body : null;
  const multiSelectStyles = {
    container: (base) => ({ ...base, width: "100%" }),
    control: (base) => ({ ...base, minHeight: 44, fontSize: "1rem" }),
    menuPortal: (base) => ({ ...base, zIndex: 9999 }),
    menu: (base) => ({ ...base, zIndex: 9999 }),
    multiValue: (base) => ({ ...base, backgroundColor: "#2196f3", color: "#fff" }),
    multiValueLabel: (base) => ({ ...base, color: "#fff" }),
    multiValueRemove: (base) => ({ ...base, color: "#fff" }),
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (tagDropdownRef.current && !tagDropdownRef.current.contains(event.target)) {
        setIsTagDropdownOpen(false);
      }
    };

    if (isTagDropdownOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isTagDropdownOpen]);

  useEffect(() => {
    const resolvedProduct =
      productData?.data?.data || productData?.data || seededProduct || null;

    if (!resolvedProduct) {
      if (!productLoading && !loadErrorNotified.current) {
        loadErrorNotified.current = true;
        setLoadError(true);
        setFormInitializing(false);
        toast.error("Unable to load product details");
      }
      return;
    }

    loadErrorNotified.current = false;
    setLoadError(false);

    setTitle(resolvedProduct.title || "");
    setSlug(resolvedProduct.slug || "");

    // MacroFamily: support array or single object
    if (Array.isArray(resolvedProduct.macroFamily)) {
      setMacroFamilyId(resolvedProduct.macroFamily.map(mf => normalizeId(mf)));
    } else if (resolvedProduct.macroFamily) {
      setMacroFamilyId([normalizeId(resolvedProduct.macroFamily)]);
    } else {
      setMacroFamilyId([]);
    }
    setCategoryId(normalizeId(resolvedProduct.category));

    const industrySource =
      resolvedProduct.industries ?? resolvedProduct.industry ?? [];
    if (Array.isArray(industrySource)) {
      setIndustryId(industrySource.map(ind => normalizeId(ind)));
    } else if (industrySource) {
      setIndustryId([normalizeId(industrySource)]);
    } else {
      setIndustryId([]);
    }

    const tagSource = resolvedProduct.tags;
    if (tagSource) {
      const tagIds = (Array.isArray(tagSource) ? tagSource : [tagSource])
        .map((tag) => normalizeId(tag))
        .filter(Boolean);
      setTags(tagIds);
    } else {
      setTags([]);
    }

    if (resolvedProduct.thumbnail_image) {
      setThumbnailPreview(buildAssetUrl(resolvedProduct.thumbnail_image));
    } else {
      setThumbnailPreview("");
    }
    setThumbnailFile(null);

    const media = resolvedProduct.media || {};
    setExistingImages(
      Array.isArray(media.images)
        ? media.images
            .map((img) => extractMediaPath(img))
            .filter(Boolean)
        : []
    );
    setNewImageFiles([]);
    setNewImagePreviews([]);

    setVideoEntries(
      Array.isArray(media.videos)
        ? media.videos
            .map((video) => normalizeVideoEntry(video))
            .filter((video) => video.url)
        : []
    );
    setVideoUrlInput("");
    setVideoTitleInput("");
    setVideoDescriptionInput("");

    if (Array.isArray(media.catalogs)) {
      setExistingCatalogs(media.catalogs.map((file) => extractMediaPath(file)).filter(Boolean));
      setExistingCatalogTitles(media.catalogs.map((file) => file?.title || ""));
      setExistingCatalogDescriptions(media.catalogs.map((file) => file?.description || ""));
    } else {
      setExistingCatalogs([]);
      setExistingCatalogTitles([]);
      setExistingCatalogDescriptions([]);
    }
    setNewCatalogFiles([]);
    setNewCatalogPreviews([]);
    setNewCatalogTitles([]);
    setNewCatalogDescriptions([]);

    setSelectedImage("");
    setSelectedImageMeta(null);
    setIsModalOpen(false);

    setFormInitializing(false);
  }, [productData, seededProduct, productLoading]);

  const handleToggleTag = (tagValue) => {
    if (!tagValue) return;
    if (tags.includes(tagValue)) {
      setTags(tags.filter((tag) => tag !== tagValue));
    } else {
      setTags([...tags, tagValue]);
    }
  };

  const handleRemoveTag = (tagValue) => {
    setTags(tags.filter((tag) => tag !== tagValue));
  };

  const readThumbnailURL = (input) => {
    if (input.files && input.files[0]) {
      const file = input.files[0];
      setThumbnailFile(file);
      const reader = new FileReader();
      reader.onload = function (e) {
        setThumbnailPreview(e.target?.result || "");
      };
      reader.readAsDataURL(file);
    }
  };

  const handleBulkImageChange = (e) => {
    if (e.target.files) {
      const files = Array.from(e.target.files);
      const validImageTypes = ["image/jpeg", "image/png", "image/jpg", "image/gif", "image/webp"];
      const validExtensions = [".jpg", ".jpeg", ".png", ".gif", ".webp"];
      const validFiles = [];
      const invalidFiles = [];

      files.forEach((file) => {
        const ext = file.name.substring(file.name.lastIndexOf('.')).toLowerCase();
        if (validImageTypes.includes(file.type) && validExtensions.includes(ext)) {
          validFiles.push(file);
        } else {
          invalidFiles.push(file.name);
        }
      });

      if (invalidFiles.length > 0) {
        toast.error("Only image files are supported (JPG, PNG, GIF, WebP). PDF and other formats are not allowed.", { autoClose: 5000 });
      }

      if (validFiles.length > 0) {
        const previews = validFiles.map((file) => URL.createObjectURL(file));
        setNewImageFiles((prev) => [...prev, ...validFiles]);
        setNewImagePreviews((prev) => [...prev, ...previews]);
      }
    }
  };

  const handleImageClick = (img, meta) => {
    setSelectedImage(img);
    setSelectedImageMeta(meta);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedImage("");
    setSelectedImageMeta(null);
  };

  const handleDeleteSelectedImage = () => {
    if (!selectedImageMeta) return;
    if (selectedImageMeta.type === "existing") {
      setExistingImages((prev) =>
        prev.filter((_, index) => index !== selectedImageMeta.index)
      );
    } else {
      const indexToRemove = selectedImageMeta.index;
      const previewUrl = newImagePreviews[indexToRemove];
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl);
      }
      setNewImageFiles((prev) =>
        prev.filter((_, index) => index !== indexToRemove)
      );
      setNewImagePreviews((prev) =>
        prev.filter((_, index) => index !== indexToRemove)
      );
    }
    closeModal();
  };

  const handleCatalogueFileChange = (e) => {
    if (e.target.files) {
      const files = Array.from(e.target.files);
      const previews = files.map((file) => ({
        name: file.name,
        size: file.size,
      }));
      setNewCatalogFiles((prev) => [...prev, ...files]);
      setNewCatalogPreviews((prev) => [...prev, ...previews]);
      setNewCatalogTitles((prev) => [...prev, ...files.map(() => "")]);
      setNewCatalogDescriptions((prev) => [...prev, ...files.map(() => "")]);
    }
  };

  const handleRemoveCatalogFile = (indexToRemove) => {
    setNewCatalogFiles((prev) => prev.filter((_, index) => index !== indexToRemove));
    setNewCatalogPreviews((prev) => prev.filter((_, index) => index !== indexToRemove));
    setNewCatalogTitles((prev) => prev.filter((_, index) => index !== indexToRemove));
    setNewCatalogDescriptions((prev) => prev.filter((_, index) => index !== indexToRemove));
  };

  const handleRemoveExistingCatalog = (indexToRemove) => {
    setExistingCatalogs((prev) => prev.filter((_, index) => index !== indexToRemove));
    setExistingCatalogTitles((prev) => prev.filter((_, index) => index !== indexToRemove));
    setExistingCatalogDescriptions((prev) => prev.filter((_, index) => index !== indexToRemove));
  };

  const getEmbedUrl = (url) => {
    if (
      typeof url === "string" &&
      (url.includes("youtube.com") || url.includes("youtu.be"))
    ) {
      let videoId = "";
      if (url.includes("watch?v=")) {
        videoId = url.split("watch?v=")[1].split("&")[0];
      } else if (url.includes("youtu.be/")) {
        videoId = url.split("youtu.be/")[1].split("?")[0];
      }
      return `https://www.youtube.com/embed/${videoId}`;
    }
    return null;
  };

  const handleAddVideoUrl = (e) => {
    e.preventDefault();
    const trimmed = videoUrlInput.trim();
    if (!trimmed) return;
    setVideoEntries((prev) => [
      ...prev,
      {
        url: trimmed,
        title: videoTitleInput.trim(),
        description: videoDescriptionInput.trim(),
      },
    ]);
    setVideoUrlInput("");
    setVideoTitleInput("");
    setVideoDescriptionInput("");
  };

  const handleVideoFieldChange = (indexToUpdate, field, value) => {
    setVideoEntries((prev) =>
      prev.map((video, index) =>
        index === indexToUpdate ? { ...video, [field]: value } : video
      )
    );
  };

  const handleRemoveVideo = (indexToRemove) => {
    setVideoEntries((prev) => prev.filter((_, index) => index !== indexToRemove));
  };

  const handleSubmit = async (e) => {
    if (e?.preventDefault) {
      e.preventDefault();
    }
    if (submitting) return;
    if (!productId) {
      toast.error("Missing product identifier");
      return;
    }

    // Validation and scroll to first error
    if (!title.trim()) {
      toast.error("Title is required");
      titleRef.current?.focus();
      titleRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
      return;
    }
    if (!slug.trim()) {
      toast.error("Slug is required");
      slugRef.current?.focus();
      slugRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
      return;
    }

    setSubmitting(true);
    const formData = new FormData();

    try {
      formData.append("title", title.trim());
      formData.append("slug", slug.trim());
      formData.append("category", categoryId || "");
      macroFamilyId.forEach(mf => formData.append("macroFamily", mf));
      industryId.forEach(ind => formData.append("industries", ind));

      const tagIds = tags.filter(Boolean);
      tagIds.forEach(tag => formData.append("tags", tag));

      if (thumbnailFile) {
        formData.append("thumbnail_image", thumbnailFile);
      }

      const existingImageEntries = await convertExistingAssets(
        existingImages,
        "image"
      );
      existingImageEntries.forEach((item) => {
        if (item.type === "file" && item.value) {
          formData.append("media.images", item.value);
        } else if (item.type === "path" && item.value) {
          formData.append("media.images", item.value);
        }
      });

      newImageFiles.forEach((file) => {
        formData.append("media.images", file);
      });

      videoEntries.forEach((video, index) => {
        const normalizedVideo = normalizeVideoEntry(video);
        const trimmedUrl = normalizedVideo.url.trim();

        if (!trimmedUrl) return;

        formData.append(`media.videos[${index}][url]`, trimmedUrl);
        formData.append(`media.videos[${index}][title]`, normalizedVideo.title.trim());
        formData.append(`media.videos[${index}][description]`, normalizedVideo.description.trim());
      });

      const existingCatalogEntries = await convertExistingAssets(
        existingCatalogs,
        "catalog"
      );
      existingCatalogEntries.forEach((item, index) => {
        if (item.type === "file" && item.value) {
          formData.append(`media.catalogs[${index}].url`, item.value);
        } else if (item.type === "path" && item.value) {
          formData.append(`media.catalogs[${index}].url`, item.value);
        }
        formData.append(`media.catalogs[${index}].title`, existingCatalogTitles[index] || "");
        formData.append(`media.catalogs[${index}].description`, existingCatalogDescriptions[index] || "");
      });

      newCatalogFiles.forEach((file, index) => {
        const baseIndex = existingCatalogs.length + index;
        formData.append(`media.catalogs[${baseIndex}].url`, file);
        formData.append(`media.catalogs[${baseIndex}].title`, newCatalogTitles[index] || "");
        formData.append(`media.catalogs[${baseIndex}].description`, newCatalogDescriptions[index] || "");
      });

      updateProduct(
        { id: productId, body: formData },
        {
          onSuccess: () => {
            toast.success("Product updated successfully!");
            setSubmitting(false);
            navigate(`/products/view/${productId}`);
          },
          onError: (error) => {
            toast.error(error?.message || "Failed to update product");
            setSubmitting(false);
          },
        }
      );
    } catch (error) {
      console.error("Failed to prepare update payload", error);
      toast.error("Unable to prepare product update");
      setSubmitting(false);
    }
  };

  if (formInitializing) {
    return <div className="loading">Loading product data...</div>;
  }

  if (loadError) {
    return <div className="loading">Unable to load product data.</div>;
  }

  const getTagValue = (tag) => {
    if (typeof tag === "string") return tag;
    return tag?._id || tag?.id || tag?.value || "";
  };

  const getTagLabel = (tagId) => {
    const match = (tagOptions || []).find((tag) => {
      if (typeof tag === "string") return tag === tagId;
      return (
        tag?._id === tagId ||
        tag?.id === tagId ||
        tag?.value === tagId ||
        tag?.slug === tagId
      );
    });
    if (!match) return tagId;
    return typeof match === "string" ? match : match.title || match.name || tagId;
  };

  return (
    <>
      <div className="body">
        <form onSubmit={handleSubmit}>
          <div className="inputFields">
            <div className="form-header">
              <div
                className="text-inputs"
                style={{
                  flex: 1,
                  display: "flex",
                  flexDirection: "column",
                  gap: "20px",
                }}
              >
                <div>
                  <label htmlFor="title">Title</label>
                  <input
                    type="text"
                    id="title"
                    placeholder="Text here..."
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    style={{ width: "100%", marginTop: "10px" }}
                    ref={titleRef}
                  />
                </div>
                <div>
                  <label htmlFor="slug">Slug</label>
                  <input
                    type="text"
                    id="slug"
                    placeholder="Text here..."
                    value={slug}
                    onChange={(e) => setSlug(e.target.value)}
                    style={{ width: "100%", marginTop: "10px", marginBottom: "10px" }}
                    ref={slugRef}
                  />
                </div>
              </div>
              <div className="image-upload-section">
                <div className="avatar-upload" style={{ margin: 0 }}>
                  <div className="avatar-edit">
                    <input
                      type="file"
                      id="imageUpload"
                      accept=".png, .jpg, .jpeg"
                      onChange={(e) => readThumbnailURL(e.target)}
                    />
                    <label htmlFor="imageUpload"></label>
                  </div>
                  <div className="avatar-preview">
                    {console.log('Thumbnail preview URL:', thumbnailPreview)}
                    <div
                      id="imagePreview"
                      style={{ backgroundImage: thumbnailPreview ? `url(${thumbnailPreview})` : 'url(/default-thumbnail.png)' }}
                    ></div>
                    {!thumbnailPreview && (
                      <img src="/default-thumbnail.png" alt="No Thumbnail" style={{ width: '100%', height: '100%', borderRadius: '30px', objectFit: 'cover' }} />
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="select-container">
            <div className="form">
              <label>Select Choices</label>
              <label style={{ textAlign: "left", marginBottom: "10px", fontWeight: "bold", fontSize: "1rem" }}>Macro Family</label>
              <Select
                isMulti
                options={macrofamilyOptions.map(option => ({ value: normalizeId(option), label: getOptionLabel(option), ...option }))}
                value={macroFamilyId.map(id => {
                  const mfObj = macrofamilyOptions.find(opt => normalizeId(opt) === id);
                  return mfObj ? { value: normalizeId(mfObj), label: getOptionLabel(mfObj), ...mfObj } : null;
                }).filter(Boolean)}
                onChange={selected => setMacroFamilyId(selected.map(opt => opt.value))}
                placeholder="Select one or more macro families..."
                menuPortalTarget={selectMenuPortalTarget}
                menuPosition="fixed"
                styles={multiSelectStyles}
              />
              <select
                name="category"
                id="category"
                value={categoryId}
                onChange={(e) => setCategoryId(e.target.value)}
              >
                <option value="">---Select Category---</option>
                {categoryOptions.map((option) => {
                  const optionValue = normalizeId(option) || getOptionLabel(option);
                  return (
                    <option key={optionValue} value={optionValue}>
                      {getOptionLabel(option)}
                    </option>
                  );
                })}
              </select>
              <label style={{ textAlign: "left", marginBottom: "10px", fontWeight: "bold", fontSize: "1rem" }}>Industry</label>
              <Select
                isMulti
                options={industryOptions.map(option => ({ value: normalizeId(option), label: getOptionLabel(option), ...option }))}
                value={industryId.map(id => {
                  const indObj = industryOptions.find(opt => normalizeId(opt) === id);
                  return indObj ? { value: normalizeId(indObj), label: getOptionLabel(indObj), ...indObj } : null;
                }).filter(Boolean)}
                onChange={selected => setIndustryId(selected.map(opt => opt.value))}
                placeholder="Select one or more industries..."
                menuPortalTarget={selectMenuPortalTarget}
                menuPosition="fixed"
                styles={multiSelectStyles}
              />

              <div className="modal-tags-container">
                <div style={{ marginBottom: "20px" }}>
                  <label htmlFor="tagInput" style={{ textAlign: "left" }}>
                    Select Tag
                  </label>
                  <Select
                    isMulti
                    options={tagOptions.map(option => ({ value: getTagValue(option), label: getTagLabel(getTagValue(option)) }))}
                    value={tags.map(tagId => {
                      const tagObj = tagOptions.find(opt => getTagValue(opt) === tagId);
                      return tagObj ? { value: getTagValue(tagObj), label: getTagLabel(getTagValue(tagObj)) } : null;
                    }).filter(Boolean)}
                    onChange={selected => setTags(selected.map(opt => opt.value))}
                    placeholder="Select one or more tags..."
                    menuPortalTarget={selectMenuPortalTarget}
                    menuPosition="fixed"
                    styles={multiSelectStyles}
                  />
                </div>
              </div>
            </div>
          </div>

          <div className="uploadSection">
            <div className="UploadImages">
              <div className="bulkThumbnail">
                <h3>Bulk Image Upload</h3>
                <div className="bulkUpload">
                  <div className="bulk-images-grid">
                    {existingImages.map((img, index) => {
                      const url = buildAssetUrl(img);
                      return (
                        <div key={`existing-${index}`} className="bulk-image-card">
                          <img
                            src={url}
                            alt={`existing-${index}`}
                            onClick={() => handleImageClick(url, { type: "existing", index })}
                            style={{ cursor: "pointer" }}
                          />
                        </div>
                      );
                    })}
                    {newImagePreviews.map((img, index) => (
                      <div key={`new-${index}`} className="bulk-image-card">
                        <img
                          src={img}
                          alt={`upload-${index}`}
                          onClick={() =>
                            handleImageClick(img, { type: "new", index })
                          }
                          style={{ cursor: "pointer" }}
                        />
                      </div>
                    ))}
                    <div
                      className="add-new-card"
                      onClick={() =>
                        document.getElementById("bulkImageInput").click()
                      }
                    >
                      <span>+ Add New</span>
                    </div>
                  </div>
                  <input
                    type="file"
                    multiple
                    id="bulkImageInput"
                    style={{ display: "none" }}
                    onChange={handleBulkImageChange}
                  />
                </div>
              </div>
            </div>

            <div className="fileUpload">
              <div className="upload-header">
                <h3>Upload Catalogues</h3>
              </div>

              <div className="upArea">
                <div
                  className="upload-area"
                  onClick={() =>
                    document.getElementById("catalogueFileInput").click()
                  }
                >
                  <div className="plus-icon">+</div>
                  <p>Click or drag files to upload</p>
                  <p className="file-info">PDF or DOC files only (20MB Max)</p>
                </div>

                <input
                  type="file"
                  id="catalogueFileInput"
                  accept=".pdf,.doc,.docx"
                  multiple
                  onChange={handleCatalogueFileChange}
                  style={{ display: "none" }}
                />

                {(existingCatalogs.length > 0 || newCatalogPreviews.length > 0) && (
                  <div className="file-list">
                    {existingCatalogs.map((file, index) => (
                      <div key={`existing-cat-${index}`} className="file-item">
                        <div className="file-info-row">
                          <div className="file-icon">📄</div>
                          <div className="file-details">
                            <div className="file-name">{getFileName(file)}</div>
                            <input
                              type="text"
                              placeholder="Catalogue title"
                              value={existingCatalogTitles[index] || ""}
                              onChange={e => {
                                const val = e.target.value;
                                setExistingCatalogTitles(prev => prev.map((t, i) => i === index ? val : t));
                              }}
                              style={{ marginTop: 4, marginBottom: 4, width: "100%" }}
                            />
                            <input
                              type="text"
                              placeholder="Catalogue description"
                              value={existingCatalogDescriptions[index] || ""}
                              onChange={e => {
                                const val = e.target.value;
                                setExistingCatalogDescriptions(prev => prev.map((d, i) => i === index ? val : d));
                              }}
                              style={{ marginBottom: 4, width: "100%" }}
                            />
                            <div className="file-meta">
                              <a
                                href={buildAssetUrl(file)}
                                target="_blank"
                                rel="noreferrer"
                                className="btn"
                              >
                                Download
                              </a>
                            </div>
                          </div>
                        </div>
                        <div className="file-actions">
                          <button
                            className="delete-btn"
                            type="button"
                            onClick={() => handleRemoveExistingCatalog(index)}
                          >
                            <RiDeleteBin5Line />
                          </button>
                        </div>
                      </div>
                    ))}
                    {newCatalogPreviews.map((file, index) => (
                      <div key={`new-cat-${index}`} className="file-item">
                        <div className="file-info-row">
                          <div className="file-icon">📄</div>
                          <div className="file-details">
                            <div className="file-name">{file.name}</div>
                            <input
                              type="text"
                              placeholder="Catalogue title"
                              value={newCatalogTitles[index] || ""}
                              onChange={e => {
                                const val = e.target.value;
                                setNewCatalogTitles(prev => prev.map((t, i) => i === index ? val : t));
                              }}
                              style={{ marginTop: 4, marginBottom: 4, width: "100%" }}
                            />
                            <input
                              type="text"
                              placeholder="Catalogue description"
                              value={newCatalogDescriptions[index] || ""}
                              onChange={e => {
                                const val = e.target.value;
                                setNewCatalogDescriptions(prev => prev.map((d, i) => i === index ? val : d));
                              }}
                              style={{ marginBottom: 4, width: "100%" }}
                            />
                            <div className="file-meta">
                              <span className="file-size">
                                {file.size ? (file.size / (1024 * 1024)).toFixed(2) : "0"} MB
                              </span>
                            </div>
                          </div>
                        </div>
                        <div className="file-actions">
                          <button
                            className="delete-btn"
                            type="button"
                            onClick={() => handleRemoveCatalogFile(index)}
                          >
                            <RiDeleteBin5Line />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="videoContainer">
            <h3>Product Videos</h3>
            <div className="bulkUpload">
              <div className="url-input-container">
                <input
                  type="text"
                  placeholder="Enter YouTube URL (e.g., https://youtu.be/...)"
                  value={videoUrlInput}
                  onChange={(e) => setVideoUrlInput(e.target.value)}
                  onKeyPress={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      handleAddVideoUrl(e);
                    }
                  }}
                />
                <input
                  type="text"
                  placeholder="Enter video title"
                  value={videoTitleInput}
                  onChange={(e) => setVideoTitleInput(e.target.value)}
                />
                <input
                  type="text"
                  placeholder="Enter video description"
                  value={videoDescriptionInput}
                  onChange={(e) => setVideoDescriptionInput(e.target.value)}
                />
                <button type="button" onClick={handleAddVideoUrl} className="commonButton">
                  Add Video
                </button>
              </div>
              <div className="bulk-video-grid">
                {videoEntries.map((video, index) => {
                  const embedUrl = getEmbedUrl(video.url);
                  return (
                    <div
                      key={`video-${index}`}
                      className="bulk-video-card editable-video-card"
                      style={{ position: "relative" }}
                    >
                      {embedUrl ? (
                        <>
                          <div className="editable-video-preview">
                            <iframe
                              width="100%"
                              height="100%"
                              src={embedUrl}
                              title={video.title || `video-${index}`}
                              frameBorder="0"
                              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                              allowFullScreen
                            ></iframe>
                          </div>
                          <button
                            className="delete-btn"
                            type="button"
                            onClick={() => handleRemoveVideo(index)}
                            style={{
                              position: "absolute",
                              top: "10px",
                              right: "10px",
                              backgroundColor: "rgba(0,0,0,0.7)",
                              color: "white",
                              border: "none",
                              borderRadius: "4px",
                              padding: "5px 10px",
                              cursor: "pointer",
                            }}
                          >
                            <RiDeleteBin5Line />
                          </button>
                          <div
                            className="editable-video-fields"
                            style={{
                              display: "flex",
                              flexDirection: "column",
                              gap: "8px",
                              marginTop: "10px",
                              width: "100%",
                            }}
                          >
                            <input
                              type="text"
                              placeholder="Video URL"
                              value={video.url}
                              onChange={(e) => handleVideoFieldChange(index, "url", e.target.value)}
                            />
                            <input
                              type="text"
                              placeholder="Video title"
                              value={video.title}
                              onChange={(e) => handleVideoFieldChange(index, "title", e.target.value)}
                            />
                            <textarea
                              placeholder="Video description"
                              value={video.description}
                              onChange={(e) => handleVideoFieldChange(index, "description", e.target.value)}
                              rows={3}
                              style={{ resize: "vertical", minHeight: "84px" }}
                            />
                          </div>
                        </>
                      ) : (
                        <div className="editable-video-fields" style={{ padding: "10px", textAlign: "center" }}>
                          <input
                            type="text"
                            placeholder="Video URL"
                            value={video.url}
                            onChange={(e) => handleVideoFieldChange(index, "url", e.target.value)}
                            style={{ marginBottom: "8px" }}
                          />
                          <input
                            type="text"
                            placeholder="Video title"
                            value={video.title}
                            onChange={(e) => handleVideoFieldChange(index, "title", e.target.value)}
                            style={{ marginBottom: "8px" }}
                          />
                          <textarea
                            placeholder="Video description"
                            value={video.description}
                            onChange={(e) => handleVideoFieldChange(index, "description", e.target.value)}
                            rows={3}
                            style={{ resize: "vertical", minHeight: "84px", marginBottom: "8px" }}
                          />
                          <button
                            className="delete-btn"
                            type="button"
                            onClick={() => handleRemoveVideo(index)}
                          >
                            <RiDeleteBin5Line />
                          </button>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          <div
            style={{
              display: "flex",
              gap: "10px",
              justifyContent: "flex-end",
              marginTop: "20px",
            }}
          >
            <button
              type="button"
              className="cancelBtn"
              onClick={() => navigate(-1)}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="commonButton"
              disabled={submitting}
            >
              {submitting ? "Saving..." : "Save Changes"}
            </button>
          </div>
        </form>
      </div>

      <Modal
        isOpen={isModalOpen}
        onClose={closeModal}
        className="image-preview-modal"
      >
        <div className="image-modal-wrapper">
          <img src={selectedImage} alt="Preview" className="modal-preview-image" />
          <button
            className="modal-delete-btn"
            onClick={handleDeleteSelectedImage}
            title="Delete image"
          >
            <RiDeleteBin5Line />
          </button>
        </div>
      </Modal>
    </>
  );
};

export default EditProducts;
